import React, { FC, useCallback, useMemo, useState } from "react";
import ScTable from "@/PageComponents/Table/ScTable";
import { ScTableProps } from "@/PageComponents/Table/table-model";
import * as Enums from '@/utils/enums';
import { Button } from "@mantine/core";
import { useRouter } from "next/router";
import {
    IconEdit,
} from "@tabler/icons";
import { IconPlus } from "@tabler/icons-react";
import UserConfigService from "@/services/option/user-config-service";
import Link from "next/link";
import Helper from "@/utils/helper";
import {getEnumStringValue} from "@/utils/enums";
import PurchaseOrderDrawer from "@/PageComponents/Purchases/PurchaseOrderDrawer";
import { useDidUpdate } from "@mantine/hooks";

const PurchasesTable: FC<{itemId: string; customerId: string; module: number; accessStatus?: any; onSaved?: (purchaseOrder: any) => void; onCreateNew?: () => void; onCopyToPurchaseOrder?: (purchaseOrder: any) => void; refreshToken?: number}> = (props: any) => {

    const purchaseTableProps = useMemo<ScTableProps>(() => (
        {
            bypassGlobalState: true,
            authUserConfig: async () => await UserConfigService.getPageFilters(Enums.ConfigurationSection.PurchaseOrder),
            columnMappingModelName: 'PurchaseOrderList',
            columMappingOverrideValues: {
                EmployeeFullName: {
                    MetaData: JSON.stringify({
                        displayColorKeyName: 'EmployeeDisplayColor'
                    })
                },
                PurchaseOrderNumber: {
                    CellType: 'link',
                    MetaData: JSON.stringify({
                        href: '/purchase/',
                        slug: 'ID',
                        triggerAction: 'openDrawer'
                    })
                },
                IsClosed: {
                    // because the label is "Open"
                    InverseLogic: true
                },
                PurchaseOrderStatus: {
                    MetaData: JSON.stringify({
                        mappingValues: Enums.PurchaseOrderStatus,
                        colourMappingValues: Enums.PurchaseOrderStatusColor
                    })
                },
                Module: {
                    MetaData: JSON.stringify({
                        mappingValues: Enums.Module,
                        // colourMappingValues: Enums.Status
                    })
                },
                PurchaseOrderSyncStatus: {
                    MetaData: JSON.stringify({
                        mappingValues: Enums.SyncStatus,
                        // colourMappingValues: Enums.Status
                    })
                },
            },
            tableDataEndpoint: '/PurchaseOrder/GetPurchaseOrders',
            tableName: 'itemPurchase' + props.module + (props.itemId ? '_' + props.itemId : '') + (props.customerId ? '_' + props.customerId : ''),
            tableNoun: 'Purchase Order',
            tableFilterMetaData: {
                options: [
                    {
                        type: 'switch',
                        label: 'Include Cancelled',
                        filterName: 'IncludeCancelled',
                        inclusion: 'inclusive',
                        dataValueKey: 'IsClosed'
                    },
                    {
                        type: 'hidden',
                        filterName: 'PopulatedList',
                        label: "Populate Purchase Orders",
                        defaultValue: false
                    },
                    {
                        type: 'hidden',
                        filterName: 'ModuleIDList',
                        label: "Module",
                        defaultValue: [getEnumStringValue(Enums.Module, props.module)]
                    },
                    {
                        type: 'hidden',
                        filterName: 'ItemId',
                        label: "ItemId",
                        defaultValue: props.itemId
                    },
                ],
                showIncludeDisabledOptionsToggle: false
            },
            actions: [
                {
                    type: 'default',
                    name: 'open',
                    icon: <IconEdit />,
                    label: 'Open Purchase Order',
                    default: true
                }
            ],
            bottomRightSpace: true,
            selectMode: 'none'
        }
    ), [props.itemId, props.customerId, props.module])

    const router = useRouter()

    const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<any>(null)
    const [refreshToggle, setRefreshToggle] = useState(false)

    // host-driven refresh, e.g. after a PO is created in the host's drawer
    useDidUpdate(() => {
        setRefreshToggle((p: any) => !p)
    }, [props.refreshToken])

    const onAction = useCallback((name: string, item: any) => {
        if (name === 'openDrawer') {
            setSelectedPurchaseOrder(item)
        } else if (name === 'open') {
            router.replace('/purchase/' + item.ID)
        }
    }, [])


    return (
        <>
            <PurchaseOrderDrawer
                purchaseOrder={selectedPurchaseOrder}
                show={!!selectedPurchaseOrder}
                onClose={() => setSelectedPurchaseOrder(null)}
                hostItemID={props.itemId}
                // New/Copy from the drawer's options menu go to the host's create drawer when it has one
                onCreateNew={props.onCreateNew ? () => {
                    setSelectedPurchaseOrder(null)
                    props.onCreateNew()
                } : undefined}
                onCopyToPurchaseOrder={props.onCopyToPurchaseOrder ? (po: any) => {
                    setSelectedPurchaseOrder(null)
                    props.onCopyToPurchaseOrder(po)
                } : undefined}
                onSaved={(po: any) => {
                    setRefreshToggle((p: any) => !p)
                    props.onSaved && props.onSaved(po)
                }}
                accessStatus={props.accessStatus ?? null}
            />

            <ScTable
                key={purchaseTableProps.tableName}
                {...purchaseTableProps}
                onAction={onAction}
                forceDataRefreshFlipFlop={refreshToggle}
                activeItemId={selectedPurchaseOrder?.ID}
            >
                {
                    props.onCreateNew ?
                        <Button color={'scBlue'} rightSection={<IconPlus size={14} />} onClick={props.onCreateNew}>
                            Add {purchaseTableProps.tableNoun}
                        </Button> :
                        <Link
                            href={`/purchase/create?module=${props.module}&moduleID=${props.itemId}&customerID=${props.customerId}`}
                            onClick={() => Helper.nextLinkClicked('/purchase/create')}>
                            <Button color={'scBlue'} rightSection={<IconPlus size={14} />}>
                                Add {purchaseTableProps.tableNoun}
                            </Button>
                        </Link>
                }
            </ScTable>
        </>
    )
}


export default PurchasesTable
